import { Link, Styles, Text, View } from '@react-pdf/renderer';
import parse, { domToReact, HTMLReactParserOptions, DOMNode } from 'html-react-parser';

// Styles
import styles from './styles';
interface PDFRichTextProps {
  html: string;
  dotStyle?: Styles[string]; // Optional style override for the bullet dot in unordered lists
}
export const PDFRichText = ({
  html,
  dotStyle
}: PDFRichTextProps) => {
  if (!html) return null;

  // If the HTML is plain text without tags, wrap it in a paragraph
  const processedHtml = html.trim().startsWith('<') ? html : `<p>${html}</p>`;

  // Track ordered list item numbering
  let olCounter = 0;

  // For ignore underline styling from DOM
  const noUnderlineOptions: HTMLReactParserOptions = {
    replace: (domNode: DOMNode, index: number) => {
      if (domNode.type === 'tag' && domNode.name === 'u') {
        // Just render children without the underline styling
        return <>
            {domToReact((domNode.children || []) as DOMNode[], noUnderlineOptions)}
          </>;
      }
      // For any other tag, use the main options
      return options.replace?.(domNode, index);
    }
  };
  const options: HTMLReactParserOptions = {
    replace: (domNode: DOMNode) => {
      if (domNode.type === 'tag') {
        const tag = domNode.name;
        const domChildren = (domNode.children || []) as DOMNode[];
        const parentNode = domNode.parent;

        // Reset counter when processing a new ordered list
        if (tag === 'ol') {
          olCounter = 0;
        }

        // Handle list item numbering
        if (tag === 'li' && parentNode && parentNode.type === 'tag' && parentNode.name === 'ol') {
          olCounter++;
        }
        switch (tag) {
          case 'p':
            return <Text style={styles.p}>{domToReact(domChildren, options)}</Text>;
          case 'strong':
          case 'b':
            return <Text style={styles.strong}>
                {domToReact(domChildren, options)}
              </Text>;
          case 'em':
          case 'i':
            return <Text style={styles.em}>{domToReact(domChildren, options)}</Text>;
          case 'u':
            return <Text style={styles.u}>{domToReact(domChildren, options)}</Text>;
          case 'ul':
            return <View style={styles.ul}>{domToReact(domChildren, options)}</View>;
          case 'ol':
            return <View style={styles.ol}>{domToReact(domChildren, options)}</View>;
          case 'li':
            // Different rendering for ordered vs unordered lists
            if (parentNode && parentNode.type === 'tag' && parentNode.name === 'ol') {
              return <Text style={styles.numberedLi}>
                  {olCounter}. {domToReact(domChildren, options)}
                </Text>;
            }
            // Use View-based dot when dotStyle is provided (pixel-perfect alignment),
            // fallback to "•" text bullet for backward compatibility.
            if (dotStyle) {
              return <View style={styles.bulletRow}>
                  <View style={[styles.bulletDot, dotStyle]} />
                  <Text style={styles.bulletContent}>
                    {domToReact(domChildren, options)}
                  </Text>
                </View>;
            }
            return <Text style={styles.bulletLi}>
                • {domToReact(domChildren, options)}
              </Text>;
          case 'h1':
            return <Text style={styles.h1}>{domToReact(domChildren, options)}</Text>;
          case 'h2':
            return <Text style={styles.h2}>{domToReact(domChildren, options)}</Text>;
          case 'h3':
            return <Text style={styles.h3}>{domToReact(domChildren, options)}</Text>;
          case 'blockquote':
            return <View style={styles.blockquote}>
                <Text>{domToReact(domChildren, options)}</Text>
              </View>;
          case 'pre':
            return <View style={styles.pre}>{domToReact(domChildren, options)}</View>;
          case 'code':
            return <Text style={styles.code}>
                {domToReact(domChildren, options)}
              </Text>;
          case 'a':
            return <Link style={styles.a} href={domNode.attribs?.href || '#'}>
                <Text>{domToReact(domChildren, noUnderlineOptions)}</Text>
              </Link>;
          default:
            return <Text>{domToReact(domChildren, options)}</Text>;
        }
      }
    }
  };
  return <>{parse(processedHtml, options)}</>;
};